/**
 * The aged-receivables turn the session exhibits show (SES-001, SES-004,
 * SES-005, PGE-001, PGE-002, HRO-010, COL-009, PIL-003 and PIL-005), as the
 * application renders it: the question, the answer the run wrote, the
 * sources beneath it and the sixteen synced invoices it read.
 *
 * The figures are the board's receivables run (product-fixtures.ts): sixteen
 * open invoices, £21,541.38 outstanding as at 11 August 2026, 42.8% of it
 * overdue. Every customer name is invented for the board; none is a
 * customer, prospect or demo tenant. The question is Maya Hart's, of Calder
 * & Wynn (product-shot/fixture-calder.ts). receivables-story.test.ts checks
 * that the invoices add up to the answer and that every printed value is
 * the application's.
 */

import type { FieldOption } from "@product/app/workspace/channels/[channelId]/record-lens-utils";
import type { PluginIconPlugin } from "@product/components/content/plugin-icon";
import type { WorkspaceField } from "@product/lib/mlx-workspace-client";
import { buildRunSourceUsage, type RunSourceUsage } from "@product/lib/run-source-usage";
import type { TaskRunEvent } from "@mlx/contracts";

import type { ShotChannelTable, ShotTableCell } from "@/components/product-shot/channel-table";
import { CALDER_USER } from "@/components/product-shot/fixture-calder";
import { jsonResult } from "@/components/product-shot/replay-run";
import type { ShotTurnTimes } from "@/components/product-shot/workspace";
import { printedDate } from "./renewal-records";

const MINUTE = 60_000;

/** Tuesday 11 August 2026, 09:12 London time: a minute after the question. */
export const RECEIVABLES_NOW = Date.UTC(2026, 7, 11, 8, 12);

export const RECEIVABLES_USER = CALDER_USER;

/** Asked at 09:11, as the transcript prints it (formatDateTime, formatElapsedAgo). */
export const RECEIVABLES_TIMES: ShotTurnTimes = {
  askedAt: RECEIVABLES_NOW - MINUTE,
  dateTitle: "11 Aug 2026, 09:11",
  timeLabel: "1 min ago",
};

export const RECEIVABLES_ASK = "show aged receivables, ranked by value per customer";

/** The answer as the run wrote it. */
export const RECEIVABLES_REPLY = `### Aged receivables by customer

**As at 11 August 2026, GBP, ranked by total outstanding**

| Rank | Customer | Invoices | Total AR | Current | 1–30 days | 31–60 days | 61–90 days |
| ---: | --- | ---: | ---: | ---: | ---: | ---: | ---: |
| 1 | Tarnwick University | 1 | £6,187.50 | £0.00 | £6,187.50 | £0.00 | £0.00 |
| 2 | Orrin Street Business Services | 2 | £4,470.63 | £4,200.00 | £0.00 | £270.63 | £0.00 |
| 3 | Quayfield Club | 2 | £3,434.00 | £3,434.00 | £0.00 | £0.00 | £0.00 |
| 4 | Loxley & Venn Freight | 2 | £2,245.00 | £0.00 | £2,245.00 | £0.00 | £0.00 |
| 5 | Fennmoor Radio | 1 | £1,623.75 | £1,623.75 | £0.00 | £0.00 | £0.00 |
| 6 | Silverhook Chauffeurs | 4 | £1,169.95 | £650.00 | £0.00 | £269.95 | £250.00 |
| 7 | The Wicker Pantry | 1 | £914.55 | £914.55 | £0.00 | £0.00 | £0.00 |
| 8 | Hollis Marr Ltd | 1 | £550.00 | £550.00 | £0.00 | £0.00 | £0.00 |
| 9 | Pellbrook Media Group | 1 | £550.00 | £550.00 | £0.00 | £0.00 | £0.00 |
| 10 | Gullrock Marine Systems | 1 | £396.00 | £396.00 | £0.00 | £0.00 | £0.00 |
| | **Total** | **16** | **£21,541.38** | **£12,318.30** | **£8,432.50** | **£540.58** | **£250.00** |

- **Overdue:** £9,223.08, or **42.8%** of total receivables.
- **Collection priority:** Tarnwick University and Loxley & Venn Freight account for **£8,432.50** of the overdue balance.
- No 91+ day balances or missing due dates were found.

Basis: Xero sales invoices with a positive live \`AmountDue\`; voided and deleted
invoices excluded. Aging uses invoice due dates. The synced invoice snapshot was
last updated **11 August 2026 at approximately 06:53 UTC**.
`;

/**
 * The same answer without its table and basis, for exhibits that need the
 * reading rather than the working. The cut is made in the Markdown, so what
 * remains renders through the product unchanged.
 */
export const RECEIVABLES_REPLY_CONDENSED = (() => {
  const lines = RECEIVABLES_REPLY.split("\n");
  const basisStart = lines.findIndex((line) => line.startsWith("Basis:"));
  return (basisStart < 0 ? lines : lines.slice(0, basisStart))
    .filter((line) => !line.trimStart().startsWith("|"))
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
})();

/* ------------------------------------------------------------------ */
/*  The run's sources                                                   */
/* ------------------------------------------------------------------ */

/** The run's one call: the Xero invoices the warehouse ingested, queried for the receivables. */
const EVENTS = (
  [
    ["tool.started", { toolCallId: "call_1", toolName: "warehouse_query", pluginSlug: "xero", pluginLabel: "Xero", args: {} }],
    ["tool.completed", { toolCallId: "call_1", toolName: "warehouse_query", pluginSlug: "xero", pluginLabel: "Xero", result: jsonResult({ rows: 16 }) }],
  ] as const
).map(([kind, payload], index) => ({
  id: `evt_aged_receivables_${index + 1}`,
  runId: "run_aged_receivables",
  seq: index + 1,
  createdAt: RECEIVABLES_NOW - 50_000 + index * 1_000,
  kind,
  payload,
})) as unknown as TaskRunEvent[];

/** The sources beneath the answer, as RunSourcesUsed builds them from the run's events. */
export const RECEIVABLES_SOURCES: RunSourceUsage[] = buildRunSourceUsage(EVENTS);

export const RECEIVABLES_PLUGINS: PluginIconPlugin[] = [
  { slug: "xero", label: "Xero", branding: { iconUrl: "/concepts/product-shot/icons/sources/xero.svg" } },
];

/* ------------------------------------------------------------------ */
/*  The invoices it read                                                */
/* ------------------------------------------------------------------ */

export const INVOICE_STATUS = {
  overdue: { value: "overdue", label: "Overdue", color: "red" },
  awaiting: { value: "awaiting_payment", label: "Awaiting payment", color: "sky" },
} as const satisfies Record<string, FieldOption>;

export type Invoice = {
  number: string;
  customer: string;
  /** yyyy-mm-dd */
  dueDate: string;
  status: keyof typeof INVOICE_STATUS;
  /** In pence, so the sums are exact. */
  amountDuePence: number;
};

/**
 * The sixteen open sales invoices, by due date. Each customer's invoices sum
 * to its row of the answer, and each lands in the answer's aging bucket for
 * 11 August; how a customer's bucket splits across its invoices is
 * illustrative.
 */
export const INVOICES: readonly Invoice[] = [
  { number: "INV-0018", customer: "Silverhook Chauffeurs", dueDate: "2026-05-30", status: "overdue", amountDuePence: 25_000 },
  { number: "INV-0021", customer: "Silverhook Chauffeurs", dueDate: "2026-06-26", status: "overdue", amountDuePence: 15_000 },
  { number: "INV-0022", customer: "Orrin Street Business Services", dueDate: "2026-06-26", status: "overdue", amountDuePence: 27_063 },
  { number: "INV-0024", customer: "Silverhook Chauffeurs", dueDate: "2026-07-08", status: "overdue", amountDuePence: 11_995 },
  { number: "INV-0026", customer: "Loxley & Venn Freight", dueDate: "2026-07-22", status: "overdue", amountDuePence: 125_000 },
  { number: "INV-0027", customer: "Tarnwick University", dueDate: "2026-07-28", status: "overdue", amountDuePence: 618_750 },
  { number: "INV-0029", customer: "Loxley & Venn Freight", dueDate: "2026-08-03", status: "overdue", amountDuePence: 99_500 },
  { number: "INV-0030", customer: "Gullrock Marine Systems", dueDate: "2026-08-15", status: "awaiting", amountDuePence: 39_600 },
  { number: "INV-0031", customer: "Hollis Marr Ltd", dueDate: "2026-08-19", status: "awaiting", amountDuePence: 55_000 },
  { number: "INV-0032", customer: "Quayfield Club", dueDate: "2026-08-20", status: "awaiting", amountDuePence: 210_000 },
  { number: "INV-0033", customer: "Silverhook Chauffeurs", dueDate: "2026-08-24", status: "awaiting", amountDuePence: 65_000 },
  { number: "INV-0034", customer: "Fennmoor Radio", dueDate: "2026-08-25", status: "awaiting", amountDuePence: 162_375 },
  { number: "INV-0035", customer: "Pellbrook Media Group", dueDate: "2026-08-27", status: "awaiting", amountDuePence: 55_000 },
  { number: "INV-0036", customer: "Orrin Street Business Services", dueDate: "2026-08-30", status: "awaiting", amountDuePence: 420_000 },
  { number: "INV-0037", customer: "The Wicker Pantry", dueDate: "2026-09-02", status: "awaiting", amountDuePence: 91_455 },
  { number: "INV-0038", customer: "Quayfield Club", dueDate: "2026-09-05", status: "awaiting", amountDuePence: 133_400 },
];

function invoiceField(
  key: string,
  label: string,
  type: WorkspaceField["type"],
  config: NonNullable<WorkspaceField["config"]> = {},
): WorkspaceField {
  // Synced from Xero: the source owns every value, so each reads as it is.
  return { id: `fld_${key}`, key, label, type, required: false, status: "active", config: { valueOwner: "workflow", ...config } };
}

export const INVOICE_FIELDS: readonly WorkspaceField[] = [
  invoiceField("number", "Invoice", "text", { primary: true }),
  invoiceField("customer", "Customer", "text"),
  invoiceField("due_date", "Due date", "date"),
  invoiceField("status", "Status", "select", { options: Object.values(INVOICE_STATUS) }),
  // A finance view pins its pence (the field's precision), so every row reads alike.
  invoiceField("amount_due", "Amount due", "currency", { currency: "GBP", precision: 2 }),
];

const penceFormat = new Intl.NumberFormat("en-GB", {
  style: "currency",
  currency: "GBP",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** A sum of pence as the answer prints it: "£21,541.38". */
export function printedPounds(pence: number): string {
  return penceFormat.format(pence / 100);
}

export function invoiceValues(invoice: Invoice): Record<string, unknown> {
  return {
    number: invoice.number,
    customer: invoice.customer,
    due_date: invoice.dueDate,
    status: INVOICE_STATUS[invoice.status].value,
    amount_due: invoice.amountDuePence / 100,
  };
}

/** Each value as formatFieldValue prints it; the test compares the two. */
export function invoiceDisplays(invoice: Invoice): Record<string, string> {
  return {
    number: invoice.number,
    customer: invoice.customer,
    due_date: printedDate(invoice.dueDate),
    status: INVOICE_STATUS[invoice.status].label,
    amount_due: printedPounds(invoice.amountDuePence),
  };
}

function invoiceCell(invoice: Invoice, key: string): ShotTableCell {
  const display = invoiceDisplays(invoice)[key];
  if (display === undefined) throw new Error(`${invoice.number} prints no ${key}.`);
  return key === "status" ? { display, option: INVOICE_STATUS[invoice.status], title: display } : { display, title: display };
}

function invoiceFieldByKey(key: string): WorkspaceField {
  const found = INVOICE_FIELDS.find((candidate) => candidate.key === key);
  if (!found) throw new Error(`Sales invoices have no ${key} field.`);
  return found;
}

/** The synced invoices as a saved table view, by due date. */
export const INVOICE_TABLE: ShotChannelTable = {
  channel: { id: "ch_finance", name: "Finance" },
  dataSetName: "Sales invoices",
  viewName: "Open invoices",
  fields: INVOICE_FIELDS,
  sortField: invoiceFieldByKey("due_date"),
  rows: INVOICES.map((invoice) => ({
    id: `rec_${invoice.number.toLowerCase()}`,
    title: invoice.number,
    discussionCommentCount: 0,
    cells: Object.fromEntries(INVOICE_FIELDS.map((entry) => [entry.key, invoiceCell(invoice, entry.key)])),
  })),
};

/** The total the ledger holds, the same figure the answer carries. */
export const INVOICES_TOTAL = printedPounds(INVOICES.reduce((sum, invoice) => sum + invoice.amountDuePence, 0));
